import { Controller } from "@hotwired/stimulus";
import {
  AD_CONSTANTS,
  checkAdTiming,
  updateAdViewDate,
  createAdObserver,
} from '../utils/adUtils'

/**
 * Controller for showing ads after user scrolls past a certain point
 */
export default class extends Controller {
  static values = {
    showLimitKey: String,
  }

  /** @type {boolean} */
  #ticking = false

  /** @type {boolean} */
  #scrollLimitPassed = false

  /** @type {MutationObserver} */
  #observer

  /** @type {string[]} */
  #headerElements = [
    'header-mobile-logo',
    'header-mobile-menu',
    'header-tablet-logo',
    'header-tablet-menu',
  ]

  /** @type {number} */
  static SCROLL_LIMIT = 1200

  connect() {
    window.addEventListener('scroll', this.#handleScroll.bind(this), {
      passive: true,
    })
  }

  disconnect() {
    this.hideElements()
    window.removeEventListener('scroll', this.#handleScroll.bind(this), {
      passive: true,
    })
    if (this.#observer) {
      this.#observer.disconnect()
    }
  }

  /**
   * Handle scroll events using requestAnimationFrame for performance
   */
  #handleScroll() {
    if (!this.#ticking) {
      window.requestAnimationFrame(() => {
        this.#maybeLoadAds()
        this.#ticking = false
      })
      this.#ticking = true
    }
  }

  /**
   * Check scroll position and maybe load ads if conditions are met
   */
  #maybeLoadAds() {
    const currentScroll = document.documentElement.scrollTop
    if (
      !this.#scrollLimitPassed &&
      currentScroll > this.constructor.SCROLL_LIMIT
    ) {
      this.#scrollLimitPassed = true
      if (this.#shouldLoadAds() && window.ethicalads) {
        window.ethicalads.wait.then(() => {
          const adElement = this.element.querySelector(
            `.${AD_CONSTANTS.ETHICAL_AD_CLASS}`,
          )
          if (adElement) {
            this.#handleAdsLoaded()
          } else {
            this.#observer = createAdObserver(this.element, () => {
              this.#handleAdsLoaded()
            })
          }
        })
      }
    }
  }

  /**
   * Check if ads should be loaded based on timing rules
   * @returns {boolean}
   */
  #shouldLoadAds() {
    if (!this.hasShowLimitKeyValue) return true
    return checkAdTiming(
      this.showLimitKeyValue,
      parseInt(this.showLimitKeyValue),
    )
  }

  /**
   * Handle the ad loading process
   */
  #handleAdsLoaded() {
    const adElement = this.element.querySelector(
      `.${AD_CONSTANTS.ETHICAL_AD_CLASS}`,
    )
    if (!adElement) return

    this.#headerElements.forEach((id) => {
      const element = document.getElementById(id)
      element?.classList.add('pushed-down')
    })

    this.element.classList.add('show-me')
  }

  /**
   * Hide elements and remove header adjustments
   */
  hideElements() {
    this.element.classList.remove('show-me')
    this.#headerElements.forEach((id) => {
      const element = document.getElementById(id)
      element?.classList.remove('pushed-down')
    })
  }

  /**
   * Hide the ad and update last viewed time
   */
  hide() {
    this.hideElements()
    if (this.hasShowLimitKeyValue) {
      updateAdViewDate(this.showLimitKeyValue)
    }
  }
}
